import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { Editor, EditorModule } from 'primeng/editor';

interface EditorProps extends FormlyFieldProps {
  style?: Record<string, unknown>;
  autoResize?: boolean;
  autoResizeMaxHeight?: number;
}

export interface FormlyEditorFieldConfig
  extends FormlyFieldConfig<EditorProps> {}

@Component({
  selector: 'kon-editor-type',
  imports: [
    FormlyModule,
    FormlySelectModule,
    ReactiveFormsModule,
    EditorModule,
  ],
  template: `
    <p-editor
      #editor
      [formControl]="formControl"
      [formlyAttributes]="field"
      [style]="props.style"
      [readonly]="disabled"
      [formats]="formats"
      (onInit)="onInit()"
      (dblclick)="onDblClick($event)"
    ></p-editor>
  `,
  styles: [
    `
      ::ng-deep .p-editor .p-editor-content.ql-container {
        resize: vertical !important;
        overflow: auto !important;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyEditorTypeComponent extends FieldType<
  FieldTypeConfig<EditorProps>
> {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('editor', { static: true }) editor!: Editor;

  get disabled() {
    return !!this.props.disabled;
  }

  get editorElement() {
    return this.editor.el;
  }

  override defaultOptions?: Partial<FieldTypeConfig<EditorProps>> | undefined =
    {
      props: {
        style: {
          minHeight: '80px',
        },
        autoResize: true,
        autoResizeMaxHeight: 300,
      },
    };

  hiddenItems = ['image'];

  formats = [
    'background',
    'bold',
    'color',
    'font',
    'code',
    'italic',
    'link',
    'size',
    'strike',
    'script',
    'underline',
    'blockquote',
    'header',
    'indent',
    'list',
    'align',
    'direction',
    'code-block',
    'formula',
    'image',
  ].filter((format) => !this.hiddenItems.includes(format));

  hideToolbarItems() {
    const buttons = (this.editor.quill.theme.modules?.toolbar?.controls
      ?.filter((ctrl: [string, any]) => this.hiddenItems.includes(ctrl[0]))
      ?.map((ctrl: [string, any]) => ctrl[1]) ?? []) as HTMLButtonElement[];

    buttons.forEach((button) => (button.style.display = 'none'));
  }

  onInit() {
    this.resizeToContent();
    this.hideToolbarItems();
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => this.resizeToContent());

    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (!value) {
          this.editorElement.nativeElement.style.height = 'auto';
        }

        this.resizeToContent();
      });
  }

  resizeToContent(ignoreLimits = false) {
    if (!this.props.autoResize || !this.editorElement) {
      return;
    }

    const qlEditor = this.editorElement.nativeElement.querySelector(
      '.p-editor-content',
    ) as HTMLElement;

    const maxHeight = this.props.autoResizeMaxHeight ?? 300;
    const currentHeight = qlEditor.offsetHeight;

    if (currentHeight >= maxHeight && !ignoreLimits) {
      return;
    }

    qlEditor.style.height = 'auto';

    const newHeight = ignoreLimits
      ? qlEditor.scrollHeight
      : Math.min(qlEditor.scrollHeight, maxHeight);

    qlEditor.style.height = `${newHeight}px`;
  }

  onDblClick(event: MouseEvent) {
    // Hacky way to check if the user double-clicked on the resize handle of the editor.
    // This is not a perfect solution, but it works for most cases.
    const qlEditor = this.editorElement.nativeElement.querySelector(
      '.p-editor-content',
    ) as HTMLElement;
    const rect = qlEditor.getBoundingClientRect();
    const handleSize = 16;
    const onHandle =
      event.clientX >= rect.right - handleSize &&
      event.clientY >= rect.bottom - handleSize;

    if (onHandle) {
      this.resizeToContent(true);
    }
  }
}
